import React, { useState } from "react";
import {
  FiChevronRight,
  FiEdit2,
  FiFileText,
  FiHome,
  FiInfo,
  FiList,
  FiMail,
  FiPhone,
  FiPower,
  FiSend,
  FiShield,
  FiStar,
  FiUser,
  FiX,
} from "react-icons/fi";
import { FaStar } from "react-icons/fa";
import { FaUser } from "react-icons/fa";
import { HiOutlineCash, HiOutlineThumbUp } from "react-icons/hi";

const MENU_SECTIONS = [
  {
    title: "Payment History",
    items: [
      { id: "transactions", label: "Transactions List", icon: FiList },
      { id: "loans", label: "Loans List", icon: HiOutlineCash },
    ],
  },
  {
    title: "Support",
    items: [
      { id: "call", label: "Call Us", icon: FiPhone },
      { id: "email", label: "Send E-mail", icon: FiMail },
    ],
  },
  {
    title: "Application",
    items: [
      { id: "feedback", label: "Feedback", icon: HiOutlineThumbUp },
      { id: "share", label: "Share", icon: FiSend },
    ],
  },
  {
    title: "About",
    items: [
      { id: "about", label: "About Us", icon: FiInfo },
      { id: "terms", label: "Terms of Use", icon: FiFileText },
      { id: "privacy", label: "Privacy Policy", icon: FiShield },
    ],
  },
];

const formatProfileMobile = (mobile) => {
  const digits = String(mobile || "").replace(/\D/g, "").slice(-10);
  if (digits.length !== 10) return "+91 —";
  return `+91 ${digits}`;
};

const FwProfileScreen = ({
  name = "",
  email = "",
  mobile = "",
  avatarUrl = "",
  loading = false,
  activeTab = "profile",
  onEdit,
  onMenuClick,
  onTabChange,
  onLogout,
}) => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");

  const closeFeedback = () => {
    setFeedbackOpen(false);
    setFeedbackRating(0);
    setFeedbackText("");
  };

  const handleMenuClick = (id) => {
    if (id === "feedback") {
      setFeedbackOpen(true);
      return;
    }
    onMenuClick?.(id);
  };

  const initials = String(name || "U")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return (
    <section className="fw-screen fw-screen--profile fw-fade">
      <div className="fw-profile">
        <div className="fw-profile__card">
          <div className="fw-profile__avatar" aria-hidden="true">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" />
            ) : (
              <span>{initials || <FiUser />}</span>
            )}
          </div>
          <div className="fw-profile__info">
            {loading ? (
              <>
                <strong>Loading profile...</strong>
                <span>—</span>
                <span>+91 —</span>
              </>
            ) : (
              <>
                <strong>{name || "—"}</strong>
                <span>{email || "—"}</span>
                <span>{formatProfileMobile(mobile)}</span>
              </>
            )}
          </div>
          <button
            type="button"
            className="fw-profile__edit"
            aria-label="Edit profile"
            onClick={onEdit}
          >
            <FiEdit2 strokeWidth={1.8} />
          </button>
        </div>

        <div className="fw-profile__scroll">
          {MENU_SECTIONS.map((section) => (
            <div key={section.title} className="fw-profile__section">
              <p className="fw-profile__section-title">{section.title}</p>
              <ul className="fw-profile__menu">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className="fw-profile__row"
                        onClick={() => handleMenuClick(item.id)}
                      >
                        <span className="fw-profile__row-icon" aria-hidden="true">
                          <Icon />
                        </span>
                        <span className="fw-profile__row-label">{item.label}</span>
                        <FiChevronRight
                          className="fw-profile__row-chevron"
                          strokeWidth={1.6}
                          aria-hidden="true"
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          <button type="button" className="fw-profile__logout" onClick={onLogout}>
            <span className="fw-profile__row-icon" aria-hidden="true">
              <FiPower strokeWidth={1.8} />
            </span>
            <span className="fw-profile__row-label">Logout</span>
          </button>
        </div>
      </div>

      <nav className="fw-profile-tabbar" aria-label="Main">
        <button
          type="button"
          className={`fw-profile-tabbar__item ${activeTab === "home" ? "is-active" : ""}`}
          onClick={() => onTabChange?.("home")}
        >
          <FiHome strokeWidth={1.7} />
          <span className="fw-sr-only">Home</span>
        </button>
        <button
          type="button"
          className={`fw-profile-tabbar__item ${activeTab === "docs" ? "is-active" : ""}`}
          onClick={() => onTabChange?.("docs")}
        >
          <FiFileText strokeWidth={1.7} />
          <span className="fw-sr-only">Documents</span>
        </button>
        <button
          type="button"
          className={`fw-profile-tabbar__item fw-profile-tabbar__item--profile ${
            activeTab === "profile" ? "is-active" : ""
          }`}
          onClick={() => onTabChange?.("profile")}
        >
          <FaUser />
          <span>Profile</span>
        </button>
      </nav>
      <div className="fw-profile-home-bar" aria-hidden="true" />

      {feedbackOpen && (
        <div className="fw-feedback-overlay">
          <button
            type="button"
            className="fw-feedback-overlay__backdrop"
            aria-label="Close feedback"
            onClick={closeFeedback}
          />
          <div className="fw-feedback-modal" role="dialog" aria-labelledby="fw-feedback-title">
            <button
              type="button"
              className="fw-feedback-modal__close"
              aria-label="Close"
              onClick={closeFeedback}
            >
              <FiX />
            </button>
            <h2 id="fw-feedback-title">We appreciate your feedback.</h2>
            <p>
              We are always looking for ways to improve your experience. Please take a moment to
              evaluate and tell us what you think.
            </p>
            <div className="fw-feedback-stars" role="radiogroup" aria-label="Rating">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`fw-feedback-star ${feedbackRating >= value ? "is-on" : ""}`}
                  aria-label={`${value} star${value > 1 ? "s" : ""}`}
                  onClick={() => setFeedbackRating(value)}
                >
                  {feedbackRating >= value ? <FaStar /> : <FiStar strokeWidth={1.6} />}
                </button>
              ))}
            </div>
            <textarea
              className="fw-feedback-text"
              rows={5}
              placeholder="What can we do to improve your experience?"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
            />
            <button
              type="button"
              className="fw-feedback-submit"
              onClick={closeFeedback}
            >
              Submit Feedback
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default FwProfileScreen;
